import type { ConnectionState } from './types';

export type ConnectionListener = (state: ConnectionState) => void;

let current: ConnectionState = typeof navigator !== 'undefined' && navigator.onLine ? 'online' : 'offline';
const listeners = new Set<ConnectionListener>();

export function getConnectionState(): ConnectionState {
  return current;
}

export function setConnectionState(next: ConnectionState): void {
  if (current === next) return;
  current = next;
  listeners.forEach((fn) => {
    try {
      fn(next);
    } catch {
      /* ignore */
    }
  });
}

export function subscribeConnection(fn: ConnectionListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Bind browser online/offline events (call once per app). */
export function bindBrowserConnectionEvents(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const onOnline = () => setConnectionState('online');
  const onOffline = () => setConnectionState('offline');
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  setConnectionState(navigator.onLine ? 'online' : 'offline');
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}

/**
 * Probe local backend reachability. Prefer a cheap same-origin or gateway health URL.
 * Does not send credentials.
 */
export async function probeBackend(healthUrl: string, timeoutMs = 2500): Promise<boolean> {
  if (typeof fetch === 'undefined') return false;
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    const res = await fetch(healthUrl, {
      method: 'GET',
      cache: 'no-store',
      signal: ctrl?.signal,
    });
    return res.ok || res.status === 401 || res.status === 403;
  } catch {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function refreshConnectionFromProbe(healthUrl: string): Promise<ConnectionState> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    setConnectionState('offline');
    return 'offline';
  }
  const ok = await probeBackend(healthUrl);
  setConnectionState(ok ? 'online' : 'backend_unreachable');
  return getConnectionState();
}
